// Shared, controlled editor for a diagnostic lab's operations & schedule.
// Wraps the backend vendor_operation API shape (see diagnosticLabsService.js).
// Pure widget: knows nothing about labId / role / POST vs PUT — the wizard and
// the detail page own their own service calls. Money fields (phlebo_charge,
// phlebo_waiver, coverage_in_km) are kept as the user-typed STRING and passed
// through untouched; no client-side arithmetic on currency.
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Building2, Clock, Utensils, X } from "lucide-react";
import { WEEKDAYS, WEEKDAY_LABELS, DEFAULT_OPERATING_SLOT } from "../../services/diagnosticLabsService";

const inputCls =
  "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 disabled:bg-gray-50 disabled:border-gray-200 disabled:text-gray-700 disabled:cursor-default";

// Compact, fixed-width time picker. NOT derived from inputCls — that one carries
// `w-full`, which on the schedule rows forced every time field onto its own line.
const timeInputCls =
  "w-[116px] px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm tabular-nums focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-50 disabled:border-gray-200 disabled:text-gray-700 disabled:cursor-default";

const Field = ({ label, required, hint, error, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
      {hint && <span className="ml-1 text-xs font-normal text-gray-400">{hint}</span>}
    </label>
    {children}
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
);

const Toggle = ({ checked, onChange, disabled, label, description, icon: Icon }) => (
  <label className={`flex items-start gap-3 rounded-xl border p-4 transition-colors ${checked ? "border-blue-200 bg-blue-50/40" : "border-gray-200"} ${disabled ? "" : "cursor-pointer"}`}>
    <button type="button" role="switch" aria-checked={checked} disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className="relative inline-flex mt-0.5 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A6A6] rounded-full disabled:cursor-default">
      <span className={`block w-10 h-5 rounded-full transition-colors ${checked ? "bg-emerald-500" : "bg-gray-300"}`} />
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-5" : "translate-x-0"}`} />
    </button>
    <div className="min-w-0">
      <p className="text-sm font-medium text-gray-800 flex items-center gap-1.5">
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}{label}
      </p>
      {description && <p className="text-xs text-gray-400 mt-0.5">{description}</p>}
    </div>
  </label>
);

/* ----------------------------- data helpers ----------------------------- */

// Fresh per-day schedule with independent object refs (DEFAULT_OPERATING_SLOT
// is a module singleton — never mutate it).
const freshSlot = () =>
  WEEKDAYS.reduce((acc, day) => {
    acc[day] = { ...DEFAULT_OPERATING_SLOT[day] };
    return acc;
  }, {});

// A blank operations config (collection off, weekdays open by default).
export const emptyOperations = () => ({
  home_collection: false,
  walkin_collection: false,
  max_home: "",
  max_walkin: "",
  coverage_in_km: "",
  phlebo_charge: "",
  phlebo_waiver: "",
  female_phlebo: false,
  report_tat_in_hr: "",
  cutoff_in_min: 120,
  // One lunch break shared across the whole week (kept out of each day slot).
  lunch_start: "",
  lunch_end: "",
  // UI-only: whether every open day shares the same hours (true) or each day is
  // edited individually (false). Not part of the API payload.
  uniform_hours: true,
  operating_slot: freshSlot(),
});

// Hydrate the form value from an API operations object (or null → blank).
export const toOperationsForm = (data) => {
  if (!data) return emptyOperations();
  // Lunch is one week-wide value at the top level. Fall back to the first day
  // that carries lunch for older records that stored it per day.
  let lunch_start = data.lunch_start || "";
  let lunch_end = data.lunch_end || "";
  const slot = WEEKDAYS.reduce((acc, day) => {
    const d = data.operating_slot?.[day] || {};
    if (!lunch_start && d.lunch_start && d.lunch_end) {
      lunch_start = d.lunch_start;
      lunch_end = d.lunch_end;
    }
    acc[day] = {
      active: !!d.active,
      open_time: d.open_time || "09:00",
      close_time: d.close_time || "18:00",
    };
    return acc;
  }, {});
  // Uniform when 0/1 open days, or every open day shares identical hours.
  // contract: operating_slot[day].active is a real boolean (lab_operation_serializers.serialize_operations)
  const actives = WEEKDAYS.filter((day) => slot[day].active);
  const uniform_hours = actives.length <= 1 || actives.every(
    (day) => slot[day].open_time === slot[actives[0]].open_time && slot[day].close_time === slot[actives[0]].close_time
  );
  return {
    home_collection: !!data.home_collection,
    walkin_collection: !!data.walkin_collection,
    max_home: data.max_home ?? "",
    max_walkin: data.max_walkin ?? "",
    coverage_in_km: data.coverage_in_km ?? "",
    phlebo_charge: data.phlebo_charge ?? "",
    phlebo_waiver: data.phlebo_waiver ?? "",
    female_phlebo: !!data.female_phlebo,
    report_tat_in_hr: data.report_tat_in_hr ?? "",
    cutoff_in_min: data.cutoff_in_min ?? 120,
    lunch_start,
    lunch_end,
    uniform_hours,
    operating_slot: slot,
  };
};

// "Has the user meaningfully configured operations?" — used by the wizard to
// decide whether to POST at all (operations is optional during onboarding).
export const hasOperationsInput = (v) => !!(v && (v.home_collection || v.walkin_collection));

// "09:00" -> "9:00 AM" for human-friendly read-only display.
export const formatTime12 = (hhmm) => {
  if (!hhmm || !/^\d{1,2}:\d{2}$/.test(hhmm)) return hhmm || "";
  const [h, m] = hhmm.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
};

// Display-ready digest of a saved operations record for the detail-page summary
// card. Returns null when operations aren't configured yet.
export const operationsSummary = (data) => {
  if (!data) return null;
  // Per-day schedule in week order, with 12-hour formatted times.
  const schedule = WEEKDAYS.map((d) => {
    const s = data.operating_slot?.[d] || {};
    return {
      key: d,
      label: WEEKDAY_LABELS[d],
      active: !!s.active,
      open: formatTime12(s.open_time),
      close: formatTime12(s.close_time),
    };
  });
  // One week-wide lunch — read top-level, fall back to the first day that has it.
  let lunchStart = data.lunch_start || "";
  let lunchEnd = data.lunch_end || "";
  if (!lunchStart) {
    const dayWithLunch = WEEKDAYS.map((d) => data.operating_slot?.[d]).find((s) => s?.lunch_start && s?.lunch_end);
    if (dayWithLunch) { lunchStart = dayWithLunch.lunch_start; lunchEnd = dayWithLunch.lunch_end; }
  }
  return {
    home: !!data.home_collection,
    walkin: !!data.walkin_collection,
    femalePhlebo: !!data.female_phlebo,
    maxHome: data.max_home,
    maxWalkin: data.max_walkin,
    coverage: data.coverage_in_km,
    phleboCharge: data.phlebo_charge,
    reportTat: data.report_tat_in_hr,
    cutoff: data.cutoff_in_min,
    // contract: schedule[].active derives from operating_slot[day].active boolean (serialize_operations)
    openDays: schedule.filter((s) => s.active).map((s) => s.label.slice(0, 3)),
    schedule,
    lunch: lunchStart && lunchEnd ? `${formatTime12(lunchStart)} – ${formatTime12(lunchEnd)}` : null,
  };
};

// Build the API payload from the form value. Numbers at the boundary; decimals
// stay as strings; lunch fields only sent when both are present.
export const buildOperationsPayload = (v) => {
  const home = !!v.home_collection;
  const walkin = !!v.walkin_collection;
  const num = (x) => (x === "" || x == null ? null : Number(x));
  const decStr = (x) => (x === "" || x == null ? null : String(x).trim());

  // One lunch break for the whole week — sent ONCE at the top level, not copied
  // into every day (keeps the JSON small; the scheduler applies it to all days).
  const lunchStart = v.lunch_start || "";
  const lunchEnd = v.lunch_end || "";
  const hasLunch = !!(lunchStart && lunchEnd);

  // Working hours only — one open/close per active day, no per-day lunch.
  const operating_slot = WEEKDAYS.reduce((acc, day) => {
    const d = v.operating_slot?.[day] || {};
    acc[day] = d.active
      ? { active: true, open_time: d.open_time, close_time: d.close_time }
      : { active: false };
    return acc;
  }, {});

  return {
    home_collection: home,
    walkin_collection: walkin,
    // Off mode → send 0 (the column is non-null); on mode → the user's value.
    max_home: home ? num(v.max_home) : 0,
    max_walkin: walkin ? num(v.max_walkin) : 0,
    cutoff_in_min: v.cutoff_in_min === "" || v.cutoff_in_min == null ? 120 : Number(v.cutoff_in_min),
    report_tat_in_hr: num(v.report_tat_in_hr),
    phlebo_charge: decStr(v.phlebo_charge) ?? "0",
    phlebo_waiver: home ? decStr(v.phlebo_waiver) : null,
    female_phlebo: !!v.female_phlebo,
    coverage_in_km: home ? decStr(v.coverage_in_km) : null,
    lunch_start: hasLunch ? lunchStart : null,
    lunch_end: hasLunch ? lunchEnd : null,
    operating_slot,
  };
};

// Cross-field validation mirroring VendorOperationWriteSerializer. Returns a
// { field: message } map; empty object means valid. "HH:MM" strings are
// zero-padded and fixed-width, so lexicographic comparison == time comparison.
export const validateOperations = (v) => {
  const e = {};
  if (!v.home_collection && !v.walkin_collection) {
    e.modes = "Enable at least one of home collection or walk-in.";
  }
  if (v.report_tat_in_hr === "" || v.report_tat_in_hr == null) {
    e.report_tat_in_hr = "Report turnaround time is required.";
  } else if (Number(v.report_tat_in_hr) < 0) {
    e.report_tat_in_hr = "Cannot be negative.";
  }
  if (v.cutoff_in_min !== "" && v.cutoff_in_min != null && Number(v.cutoff_in_min) < 0) {
    e.cutoff_in_min = "Cannot be negative.";
  }
  if (v.home_collection) {
    if (v.max_home === "" || v.max_home == null || Number(v.max_home) < 1) {
      e.max_home = "Required when home collection is on (min 1).";
    }
    if (v.coverage_in_km === "" || v.coverage_in_km == null || Number(v.coverage_in_km) <= 0) {
      e.coverage_in_km = "Must be greater than 0 when home collection is on.";
    }
    if (v.phlebo_charge !== "" && v.phlebo_charge != null && Number(v.phlebo_charge) < 0) {
      e.phlebo_charge = "Cannot be negative.";
    }
    if (v.phlebo_waiver !== "" && v.phlebo_waiver != null && Number(v.phlebo_waiver) < 0) {
      e.phlebo_waiver = "Cannot be negative.";
    }
  }
  if (v.walkin_collection && (v.max_walkin === "" || v.max_walkin == null || Number(v.max_walkin) < 1)) {
    e.max_walkin = "Required when walk-in is on (min 1).";
  }

  let activeDays = 0;
  WEEKDAYS.forEach((day) => {
    const d = v.operating_slot?.[day];
    if (!d?.active) return;
    activeDays += 1;
    if (!d.open_time || !d.close_time) {
      e[`slot_${day}`] = "Open and close time are required.";
    } else if (d.open_time >= d.close_time) {
      e[`slot_${day}`] = "Open time must be before close time.";
    }
  });
  if (activeDays === 0) e.operating_slot = "At least one active day is required.";

  // One lunch break for the whole week — must be valid and fit inside every open day.
  if (v.lunch_start || v.lunch_end) {
    if (!(v.lunch_start && v.lunch_end)) {
      e.lunch = "Enter both lunch start and end.";
    } else if (v.lunch_start >= v.lunch_end) {
      e.lunch = "Lunch start must be before lunch end.";
    } else {
      // contract: operating_slot[day].active is a real boolean (lab_operation_serializers.serialize_operations)
      const outside = WEEKDAYS.some((day) => {
        const d = v.operating_slot?.[day];
        return d?.active && d.open_time && d.close_time && (v.lunch_start < d.open_time || v.lunch_end > d.close_time);
      });
      if (outside) e.lunch = "Lunch must fall within the operating hours of every open day.";
    }
  }
  return e;
};

/* ------------------------------- component ------------------------------- */

// Small inline switch reused for the per-day on/off control.
const DaySwitch = ({ checked, onChange, disabled }) => (
  <button type="button" role="switch" aria-checked={checked} disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className="relative inline-flex shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A6A6] rounded-full disabled:cursor-default">
    <span className={`block w-9 h-5 rounded-full transition-colors ${checked ? "bg-emerald-500" : "bg-gray-300"}`} />
    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-4" : "translate-x-0"}`} />
  </button>
);

// Shared transition for height/opacity reveals — quick and unobtrusive.
const reveal = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto" },
  exit: { opacity: 0, height: 0 },
  transition: { duration: 0.22, ease: "easeOut" },
};

const OperationsForm = ({ value, onChange, disabled = false, errors = {} }) => {
  const v = value || emptyOperations();
  const set = (key, val) => onChange({ ...v, [key]: val });
  const patchDay = (day, patch) =>
    onChange({
      ...v,
      operating_slot: { ...v.operating_slot, [day]: { ...v.operating_slot[day], ...patch } },
    });

  // contract: operating_slot[day].active is a real boolean (lab_operation_serializers.serialize_operations)
  const activeDayKeys = WEEKDAYS.filter((day) => v.operating_slot[day]?.active);
  const activeCount = activeDayKeys.length;
  const uniform = v.uniform_hours !== false;

  // Representative hours = the first open day's hours (fallback to a sane default).
  const first = activeDayKeys[0] ? v.operating_slot[activeDayKeys[0]] : null;
  const repHours = { open: first?.open_time || "09:00", close: first?.close_time || "18:00" };

  // Toggle a day open/closed. Opening a day seeds it with the shared hours.
  const setDayActive = (day, on) => {
    const cur = v.operating_slot[day] || {};
    patchDay(day, on
      ? { active: true, open_time: cur.open_time || repHours.open, close_time: cur.close_time || repHours.close }
      : { active: false });
  };

  // In "same hours" mode, editing one time writes it to every open day.
  const setUniformHours = (key, val) => {
    const next = WEEKDAYS.reduce((acc, day) => {
      const d = v.operating_slot[day];
      acc[day] = d?.active ? { ...d, [key]: val } : d;
      return acc;
    }, {});
    onChange({ ...v, operating_slot: next });
  };

  // Switch hours mode. Collapsing to "same" normalises every open day to repHours.
  const toggleUniform = (on) => {
    if (!on) { onChange({ ...v, uniform_hours: false }); return; }
    const next = WEEKDAYS.reduce((acc, day) => {
      const d = v.operating_slot[day];
      acc[day] = d?.active ? { ...d, open_time: repHours.open, close_time: repHours.close } : d;
      return acc;
    }, {});
    onChange({ ...v, uniform_hours: true, operating_slot: next });
  };

  // Quick day presets.
  const applyPreset = (kind) => {
    const days = kind === "weekdays"
      ? ["monday", "tuesday", "wednesday", "thursday", "friday"]
      : WEEKDAYS;
    const next = WEEKDAYS.reduce((acc, day) => {
      const d = v.operating_slot[day] || {};
      acc[day] = days.includes(day)
        ? { active: true, open_time: d.open_time || repHours.open, close_time: d.close_time || repHours.close }
        : { active: false };
      return acc;
    }, {});
    onChange({ ...v, operating_slot: next });
  };

  // One lunch break for the whole week. Toggling on seeds a sensible default;
  // toggling off clears it so it never reaches the payload.
  const lunchOn = !!(v.lunch_start || v.lunch_end);
  const toggleLunch = (on) =>
    onChange({ ...v, lunch_start: on ? "13:00" : "", lunch_end: on ? "14:00" : "" });

  return (
    <div className="space-y-6">
      {/* Collection modes */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">Collection modes</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Toggle icon={Home} label="Home collection" description="Phlebotomist visits the subscriber"
            checked={v.home_collection} disabled={disabled} onChange={(c) => set("home_collection", c)} />
          <Toggle icon={Building2} label="Walk-in collection" description="Subscriber visits the lab"
            checked={v.walkin_collection} disabled={disabled} onChange={(c) => set("walkin_collection", c)} />
        </div>
        {errors.modes && <p className="mt-1.5 text-xs text-red-500">{errors.modes}</p>}
      </div>

      {/* Capacity & home-collection params */}
      <AnimatePresence initial={false}>
        {(v.home_collection || v.walkin_collection) && (
          <motion.div {...reveal} className="overflow-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-1">
              {v.home_collection && (
                <Field label="Max home collections / day" required hint="capacity" error={errors.max_home}>
                  <input type="number" min="1" className={inputCls} disabled={disabled} value={v.max_home}
                    onChange={(e) => set("max_home", e.target.value)} placeholder="e.g. 25" />
                </Field>
              )}
              {v.walkin_collection && (
                <Field label="Max walk-ins / day" required hint="capacity" error={errors.max_walkin}>
                  <input type="number" min="1" className={inputCls} disabled={disabled} value={v.max_walkin}
                    onChange={(e) => set("max_walkin", e.target.value)} placeholder="e.g. 50" />
                </Field>
              )}
              {v.home_collection && (
                <>
                  <Field label="Coverage radius (km)" required hint="home collection" error={errors.coverage_in_km}>
                    <input type="number" min="0" step="0.1" className={inputCls} disabled={disabled} value={v.coverage_in_km}
                      onChange={(e) => set("coverage_in_km", e.target.value)} placeholder="e.g. 10" />
                  </Field>
                  <Field label="Phlebotomist charge (₹)" hint="0 = free" error={errors.phlebo_charge}>
                    <input type="number" min="0" step="0.01" className={inputCls} disabled={disabled} value={v.phlebo_charge}
                      onChange={(e) => set("phlebo_charge", e.target.value)} placeholder="e.g. 100" />
                  </Field>
                  <Field label="Phlebotomist waiver above (₹)" error={errors.phlebo_waiver}>
                    <input type="number" min="0" step="0.01" className={inputCls} disabled={disabled} value={v.phlebo_waiver}
                      onChange={(e) => set("phlebo_waiver", e.target.value)} placeholder="e.g. 500" />
                  </Field>
                  <div className="sm:col-span-2">
                    <Toggle label="Female phlebotomist available" description="Surface this to subscribers who request it"
                      checked={v.female_phlebo} disabled={disabled} onChange={(c) => set("female_phlebo", c)} />
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Service parameters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-2 border-t border-gray-100">
        <Field label="Report turnaround (hours)" required hint="typical TAT" error={errors.report_tat_in_hr}>
          <input type="number" min="0" className={inputCls} disabled={disabled} value={v.report_tat_in_hr}
            onChange={(e) => set("report_tat_in_hr", e.target.value)} placeholder="e.g. 24" />
        </Field>
        <Field label="Same-day booking cutoff (min)" error={errors.cutoff_in_min}>
          <input type="number" min="0" className={inputCls} disabled={disabled} value={v.cutoff_in_min}
            onChange={(e) => set("cutoff_in_min", e.target.value)} placeholder="120" />
        </Field>
      </div>

      {/* Weekly schedule */}
      <div className="pt-2 border-t border-gray-100">
        <div className="mb-3">
          <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-gray-400" /> Operating hours
            <span className="ml-1 px-1.5 py-0.5 rounded-md bg-[#0F4C81]/[0.07] text-[11px] font-medium text-[#0F4C81]">
              {activeCount} {activeCount === 1 ? "day" : "days"} open
            </span>
          </p>
          <p className="text-xs text-gray-400">Pick the days you operate, then set the hours.</p>
        </div>

        {/* Day picker chips + presets */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex gap-1.5">
            {WEEKDAYS.map((day) => {
              const on = !!v.operating_slot[day]?.active;
              return (
                <button key={day} type="button" disabled={disabled} title={WEEKDAY_LABELS[day]}
                  onClick={() => setDayActive(day, !on)}
                  className={`w-9 h-9 rounded-full text-xs font-semibold transition-all duration-200 disabled:opacity-60 ${on
                    ? "bg-gradient-to-br from-[#0F4C81] to-[#00A6A6] text-white shadow-sm scale-105"
                    : "bg-white text-gray-400 border border-gray-200 hover:border-[#0F4C81]/40 hover:text-[#0F4C81]"
                    }`}>
                  {WEEKDAY_LABELS[day][0]}
                </button>
              );
            })}
          </div>
          {!disabled && (
            <div className="flex gap-1.5">
              <button type="button" onClick={() => applyPreset("weekdays")}
                className="px-2.5 py-1 text-[11px] font-medium text-gray-600 border border-gray-200 rounded-lg hover:border-[#0F4C81]/40 hover:text-[#0F4C81] transition-colors">
                Mon–Fri
              </button>
              <button type="button" onClick={() => applyPreset("all")}
                className="px-2.5 py-1 text-[11px] font-medium text-gray-600 border border-gray-200 rounded-lg hover:border-[#0F4C81]/40 hover:text-[#0F4C81] transition-colors">
                All days
              </button>
            </div>
          )}
        </div>

        {errors.operating_slot && <p className="mb-2 text-xs text-red-500">{errors.operating_slot}</p>}

        {activeCount === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 py-6 text-center text-sm text-gray-400">
            Select the days the lab is open above.
          </div>
        ) : (
          <>
            {/* Hours mode */}
            <div className="inline-flex p-1 bg-gray-100 rounded-lg gap-1 mb-3">
              {[{ k: true, label: "Same hours" }, { k: false, label: "Custom per day" }].map((m) => (
                <button key={String(m.k)} type="button" disabled={disabled} onClick={() => toggleUniform(m.k)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${uniform === m.k ? "bg-white text-[#0F4C81] shadow-sm" : "text-gray-500 hover:text-gray-700"
                    }`}>
                  {m.label}
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait" initial={false}>
              {uniform ? (
                <motion.div key="uniform"
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.18 }}
                  className="rounded-xl border border-gray-200 bg-gray-50/60 px-4 py-3 flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-gray-500 mr-1">Open</span>
                  <input type="time" className={timeInputCls} disabled={disabled} value={repHours.open}
                    onChange={(e) => setUniformHours("open_time", e.target.value)} />
                  <span className="text-gray-400 text-xs">–</span>
                  <input type="time" className={timeInputCls} disabled={disabled} value={repHours.close}
                    onChange={(e) => setUniformHours("close_time", e.target.value)} />
                  <span className="text-[11px] text-gray-400 ml-auto">applied to all {activeCount} open {activeCount === 1 ? "day" : "days"}</span>
                </motion.div>
              ) : (
                <motion.div key="custom"
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.18 }}
                  className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                  {activeDayKeys.map((day, i) => {
                    const d = v.operating_slot[day];
                    const dayErr = errors[`slot_${day}`];
                    return (
                      <motion.div key={day} layout
                        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2, delay: i * 0.03, ease: "easeOut" }}
                        className="px-3 py-2.5 bg-white">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="inline-flex items-center gap-1.5 w-16 shrink-0 text-sm font-medium text-gray-800">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {WEEKDAY_LABELS[day].slice(0, 3)}
                          </span>
                          <input type="time" className={timeInputCls} disabled={disabled} value={d.open_time || ""}
                            onChange={(e) => patchDay(day, { open_time: e.target.value })} />
                          <span className="text-gray-400 text-xs">–</span>
                          <input type="time" className={timeInputCls} disabled={disabled} value={d.close_time || ""}
                            onChange={(e) => patchDay(day, { close_time: e.target.value })} />
                          {!disabled && (
                            <button type="button" aria-label={`Close ${WEEKDAY_LABELS[day]}`}
                              onClick={() => setDayActive(day, false)}
                              className="ml-auto p-1 text-gray-300 hover:text-red-500 rounded-md hover:bg-red-50 transition-colors">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        {dayErr && <p className="mt-1.5 text-xs text-red-500 pl-[76px]">{dayErr}</p>}
                      </motion.div>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        {/* One lunch break for the whole week */}
        <div className="mt-4 rounded-xl border border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="w-8 h-8 rounded-lg bg-amber-50 text-amber-500 flex items-center justify-center shrink-0">
                <Utensils className="w-4 h-4" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800">Lunch break</p>
                <p className="text-[11px] text-gray-400">One break, applied to every working day.</p>
              </div>
            </div>
            <DaySwitch checked={lunchOn} disabled={disabled} onChange={toggleLunch} />
          </div>
          <AnimatePresence initial={false}>
            {lunchOn && (
              <motion.div {...reveal} className="overflow-hidden">
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 flex-wrap text-sm">
                  <span className="text-xs text-gray-500 mr-1">From</span>
                  <input type="time" className={timeInputCls} disabled={disabled} value={v.lunch_start || ""}
                    onChange={(e) => set("lunch_start", e.target.value)} />
                  <span className="text-gray-400 text-xs">–</span>
                  <input type="time" className={timeInputCls} disabled={disabled} value={v.lunch_end || ""}
                    onChange={(e) => set("lunch_end", e.target.value)} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          {errors.lunch && <p className="mt-2 text-xs text-red-500">{errors.lunch}</p>}
        </div>
      </div>
    </div>
  );
};

export default OperationsForm;
